import { Link } from "react-router-dom";
import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, Quote, Users, Check } from "lucide-react";
import { POSTS } from "@/data/journal";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1634140144664-f84083f1090a?fm=jpg&q=85&w=2400&auto=format&fit=crop";
const METHOD_IMG =
  "https://images.unsplash.com/photo-1758933067987-ac456b5d7266?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxnb2xmJTIwc3dpbmclMjBhY3Rpb258ZW58MHx8fHwxNzc2NjczNDY3fDA&ixlib=rb-4.1.0&q=85";
const TEXTURE =
  "https://images.pexels.com/photos/6871/landscape-nature-trees-fog.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  useSeo();
  return (
    <div className="min-h-screen bg-cream text-forest-dark">
      <Header />

      {/* HERO */}
      <section data-testid="hero-section" className="relative">
        <div className="relative h-[88vh] min-h-[640px] w-full overflow-hidden">
          <img src={HERO_IMG} alt="Misty morning fairway" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest/85 via-forest/50 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto max-w-[1400px] w-full px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
              <div className="max-w-2xl text-cream-light animate-fade-up">
                <div className="eyebrow text-gold">A Coaching Marketplace for Every Player</div>
                <h1 className="mt-6 font-serif text-5xl md:text-7xl lg:text-[90px] leading-[0.95] text-cream-light">
                  Find the Right
                  <br />
                  <em className="italic text-gold font-light">Golf Coach for Your Game.</em>
                </h1>
                <p className="mt-8 max-w-xl font-sans font-light text-lg md:text-xl text-cream-light/85 leading-relaxed">
                  Golf Start connects players of all levels with experienced coaches for
                  personalized lessons, skill development, and on-course improvement.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/coaches"
                    data-testid="hero-browse-cta"
                    className="btn-primary bg-cream-light text-forest hover:bg-gold hover:text-forest"
                  >
                    Find a Coach <ArrowRight className="ml-3 h-4 w-4" />
                  </Link>
                  <Link to="/shop" data-testid="hero-shop-cta" className="btn-secondary border-cream-light text-cream-light hover:bg-cream-light hover:text-forest">
                    Shop
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee stats */}
        <div className="bg-forest text-cream-light">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              ["All Levels", "Welcome"],
              ["Personalized", "Coaching Plans"],
              ["On-Course", "Real Practice"],
              ["Pay Online", "Easy Checkout"],
            ].map(([n, l]) => (
              <div key={l} className="border-l border-cream-light/20 pl-5">
                <div className="font-serif text-3xl md:text-4xl leading-[1.1] [hyphens:none]">{n}</div>
                <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-cream-light/70">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE — editorial bento */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-28 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="eyebrow">The Player's Compact</div>
          <h2 className="mt-5 font-serif text-4xl md:text-6xl leading-[1.05] text-forest">
            Coaches vetted for every level — <em className="italic text-gold font-light">beginner to advanced.</em>
          </h2>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            { t: "Hand-picked coaches", d: "Every professional on Golf Start is vetted for teaching players at any level — first swing, weekend warrior, single-digit handicap, junior or senior." },
            { t: "Editorial profiles", d: "Read a coach's philosophy before you book. We write full essays — not 'about me' paragraphs." },
            { t: "Book online", d: "Secure checkout in under 60 seconds. Free cancellation up to 24 hours before the lesson." },
            { t: "Real ratings", d: "Only students who completed a lesson can review. Our reviews are honest, specific, and private-first." },
          ].map((b) => (
            <div key={b.t} className="border-t border-cream-border pt-6">
              <div className="rule" />
              <h3 className="mt-4 font-serif text-2xl text-forest">{b.t}</h3>
              <p className="mt-3 font-light text-forest-muted leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MEET THE COACHES — internal directory */}
      <section data-testid="meet-coach-section" className="bg-cream-light border-y border-cream-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="eyebrow">Meet the Coaches</div>
              <h2 className="mt-5 font-serif text-4xl md:text-6xl leading-[1.05] text-forest">
                Our coaches.{" "}
                <em className="italic text-gold font-light">One curated roster.</em>
              </h2>
              <p className="mt-6 max-w-xl font-serif italic text-xl text-forest-muted leading-relaxed">
                Browse every Golf Start coach, read their teaching philosophy and book the next
                open hour.
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <Link
                to="/coaches"
                data-testid="landing-find-coaches-cta"
                className="btn-primary inline-flex items-center gap-3"
              >
                Find Coaches
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "PGA-certified pros",
                d: "USGTF · TPI · PGA — every coach is credentialed and works with players from first swing to single-digit handicap.",
              },
              {
                n: "02",
                t: "Specialty matched",
                d: "Short game, juniors, course strategy or full-swing fundamentals — pick the coach whose craft fits your goal.",
              },
              {
                n: "03",
                t: "Seamless checkout",
                d: "Book and pay in a single flow on Exonome. 24-hour free cancellation standard across the roster.",
              },
            ].map((b) => (
              <div key={b.n} className="border-t border-cream-border pt-6">
                <div className="font-serif text-4xl text-gold leading-none">{b.n}</div>
                <h3 className="mt-4 font-serif text-2xl text-forest">{b.t}</h3>
                <p className="mt-3 font-light text-forest-muted leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section id="how" className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-28 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="eyebrow">The Method</div>
          <h2 className="mt-5 font-serif text-4xl md:text-6xl leading-[1.05] text-forest">
            Three steps. One better round of your life.
          </h2>
          <div className="mt-12 space-y-10">
            {[
              { n: "01", t: "Browse the roster", d: "Read essay-length profiles. Match on temperament, specialty and philosophy — beginner, intermediate, advanced or junior." },
              { n: "02", t: "Book an hour", d: "Use the coach's calendar to pick a date. Choose individual, group or on-course. No deposit required." },
              { n: "03", t: "Book online", d: "Secure checkout. Free cancellation up to 24 hours before the lesson — no questions asked." },
            ].map((s) => (
              <div key={s.n} className="grid grid-cols-12 gap-6 items-start border-t border-cream-border pt-8">
                <div className="col-span-2 font-serif text-5xl text-gold leading-none">{s.n}</div>
                <div className="col-span-10">
                  <h3 className="font-serif text-2xl text-forest">{s.t}</h3>
                  <p className="mt-2 font-light text-forest-muted leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2 relative aspect-[4/5] overflow-hidden">
          <img src={METHOD_IMG} alt="Swing study" className="h-full w-full object-cover" />
          <div className="absolute top-6 left-6 bg-cream-light px-5 py-3 border border-cream-border">
            <div className="eyebrow">Est. 2026</div>
            <div className="font-serif text-lg text-forest mt-1">San Francisco · California</div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="relative bg-forest text-cream-light overflow-hidden">
        <img src={TEXTURE} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/90 to-forest/70" />
        <div className="relative mx-auto max-w-[1100px] px-6 md:px-12 lg:px-16 py-28 md:py-40">
          <Quote className="h-10 w-10 text-gold" strokeWidth={1} />
          <blockquote className="mt-8 font-serif text-3xl md:text-5xl leading-[1.15] italic text-cream-light">
            Whether you're picking up a club for the first time or chasing a single-digit handicap, Golf Start finds
            you the right coach. One hour with mine shaved four strokes off my next round.
          </blockquote>
          <div className="mt-10 flex items-center gap-5">
            <div className="h-12 w-12 rounded-full bg-gold" />
            <div>
              <div className="font-serif text-xl text-cream-light">Jordan K.</div>
              <div className="eyebrow text-gold">Intermediate · Boston</div>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNAL */}
      <section id="journal" className="bg-cream-light border-y border-cream-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow">The Journal</div>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl text-forest leading-[1.05]">
                Latest from <em className="italic text-gold font-light">the Journal.</em>
              </h2>
              <p className="mt-5 max-w-xl font-sans text-base md:text-lg text-forest-muted leading-relaxed">
                Beginner-first guides from working coaches — fundamentals, gear, etiquette, and
                the small habits that quietly drop strokes.
              </p>
            </div>
            <Link
              to="/journal"
              data-testid="landing-journal-cta"
              className="btn-secondary inline-flex items-center gap-3"
            >
              All articles
              <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {POSTS.slice(0, 3).map((p) => (
              <Link
                key={p.slug}
                to={`/journal/${p.slug}`}
                data-testid={`landing-journal-${p.slug}`}
                className="group flex flex-col bg-cream border border-cream-border overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1 hover:border-gold"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-forest">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-4 text-[10px] tracking-[0.25em] uppercase text-forest-muted">
                    <span className="text-gold">{p.category}</span>
                    <span className="h-px w-5 bg-cream-border" />
                    <span>{p.readTime}</span>
                  </div>
                  <h3 className="mt-4 font-serif text-2xl leading-tight text-forest group-hover:text-gold transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-forest-muted line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-6 pt-5 border-t border-cream-border flex items-center justify-between">
                    <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-forest-dark group-hover:text-gold transition-colors">
                      Read more
                    </span>
                    <span className="font-sans text-xs text-forest-muted">{p.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS TEASER — Coming Soon */}
      <PartnersTeaser />

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-28 md:py-40 text-center">
        <div className="eyebrow">Begin</div>
        <h2 className="mt-6 font-serif text-5xl md:text-7xl text-forest leading-[0.95]">
          Your next breakthrough is waiting.
        </h2>
        <p className="mt-6 max-w-xl mx-auto font-light text-lg text-forest-muted">
          Create a free account and book within the hour. Beginner, intermediate, advanced, junior or adult —
          a coach who meets you exactly where you are.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link
            to="/coaches"
            data-testid="cta-browse"
            className="btn-primary inline-flex items-center gap-3"
          >
            Find Coaches
            <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
          </Link>
          <Link to="/journal" data-testid="cta-journal" className="btn-secondary">
            Read the Journal
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PartnersTeaser() {
  const [email, setEmail] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [state, setState] = useState({ status: "idle", message: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setState({ status: "error", message: "Please enter a valid email." });
      return;
    }
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/waitlist", {
        email,
        feature: "partners",
        source: "landing",
      });
      setState({
        status: "success",
        message:
          data.status === "already_subscribed"
            ? "You're already on the list — we'll be in touch."
            : "You're in. We'll write when partner matching opens.",
      });
      setEmail("");
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  return (
    <section
      data-testid="partners-teaser-section"
      className="relative bg-cream-light border-y border-cream-border"
    >
      <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <Users className="h-4 w-4 text-gold" strokeWidth={1.75} />
              <span className="eyebrow">Coming Soon — Community</span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl text-forest leading-[1.05]">
              Find <em className="italic text-gold font-light">golf partners.</em>
            </h2>
            <p className="mt-6 max-w-xl font-sans text-base md:text-lg leading-relaxed text-forest-muted">
              Connect with other golfers for practice rounds, driving range sessions and
              beginner-friendly experiences. A quieter way to find people who play at your pace.
            </p>

            {showDetails && (
              <ul
                data-testid="partners-details"
                className="mt-8 space-y-4 font-sans text-sm leading-relaxed text-forest-dark border-l-2 border-gold pl-5"
              >
                {[
                  "Match on skill level — beginner, intermediate or advanced.",
                  "Pick by preferred course or neighborhood.",
                  "Practice rounds, driving range sessions and casual meetups.",
                  "New to golf? We'll suggest a beginner lesson first so you arrive ready.",
                ].map((line) => (
                  <li key={line} className="flex gap-3 items-start">
                    <Check
                      className="h-4 w-4 text-gold mt-0.5 flex-shrink-0"
                      strokeWidth={1.75}
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <aside className="lg:col-span-5 lg:pt-2">
            <div className="border border-cream-border bg-cream p-7 md:p-8">
              <div className="eyebrow">Join the Waitlist</div>
              <p className="mt-3 font-sans text-sm text-forest-muted leading-relaxed">
                Be the first to know when partner matching opens. We'll write once — no
                marketing noise.
              </p>

              {state.status === "success" ? (
                <div
                  data-testid="waitlist-success"
                  className="mt-6 flex items-start gap-3 border border-cream-border bg-cream-light p-4"
                >
                  <Check
                    className="h-4 w-4 text-gold mt-1 flex-shrink-0"
                    strokeWidth={1.75}
                  />
                  <div className="font-sans text-sm leading-relaxed text-forest-dark">
                    {state.message}
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="mt-5 space-y-3" noValidate>
                  <label className="block">
                    <span className="sr-only">Email address</span>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      data-testid="waitlist-email-input"
                      className="w-full bg-cream-light border border-cream-border px-4 py-3 font-sans text-sm text-forest-dark placeholder:text-forest-muted/70 focus:outline-none focus:border-gold transition-colors"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={state.status === "loading"}
                    data-testid="waitlist-submit-btn"
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {state.status === "loading" ? "Joining…" : "Join the Waitlist"}
                  </button>
                  {state.status === "error" && (
                    <div
                      data-testid="waitlist-error"
                      className="font-sans text-xs text-red-700"
                    >
                      {state.message}
                    </div>
                  )}
                </form>
              )}

              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                data-testid="partners-learn-more-btn"
                className="mt-5 inline-flex items-center gap-2 font-sans text-xs tracking-[0.2em] uppercase text-forest-dark hover:text-gold transition-colors"
              >
                {showDetails ? "Hide details" : "Learn More"}
                <ArrowRight
                  className={`h-3.5 w-3.5 transition-transform ${showDetails ? "rotate-90" : ""}`}
                  strokeWidth={1.75}
                />
              </button>
            </div>

            <p className="mt-5 font-sans text-xs text-forest-muted leading-relaxed">
              In the meantime,{" "}
              <Link to="/coaches" className="underline decoration-gold underline-offset-4 hover:text-gold">
                book a beginner lesson
              </Link>{" "}
              so you're ready when matching opens.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

